import React, { Profiler, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { useActions, useStore } from "../store/index.js";
import { getSearchCriteria } from "../store/getSearchCriteria.js";
import { actions as LoaderActions } from "../store/loadingScreen/actions.js";
import { actions as searchResultsActions } from "../store/searchResults/actions.js";
import { actions as SearchCriteriaActions } from "../store/searchCriteria/actions.js";
import { actions as flexibleDateMinPriceActions } from "../store/flexibleDateMinPrice/actions.js";
import { isSirfAvailable } from "../store/selectors/sirf.js";
import { useClientFetch } from "../hooks/useClientFetch.js";
import { useSearchAPIParams } from "../hooks/useSearchAPIParams.js";
import useClientMetrics from "../hooks/useClientMetrics.js";
import { COMPANY_NAME } from "@flights/constants";
import AppShell from "../components/elements/AppShell/index.js";
import SearchHeader from "../components/elements/SearchHeader/index.js";
import SearchResults from "../components/elements/SearchResults/index.js";
import { OptInAANeetiTracker } from "../components/remote/index.js";
import { isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { FlightDetails, Home } from "../app/routes.js";
import useTrackPageLoad from "../hooks/useTrackPageLoad.js";
import useLandingPageOfferPriceAccuracyEvent from "../hooks/c360/useLandingPageOfferPriceAccuracyEvent.js";
import SirfInfoBanner from "../components/elements/SirfInfoBanner/index.js";
import useReloadIfSearchStoreMissing, { isSearchStoreInitial } from "../hooks/useReloadIfSearchStoreMissing.js";
import { DestinationUkraineWarning } from "../components/elements/TravelAlert/index.js";
import { useFlightSearchResultsGTMTag } from "../hooks/useGTMFlightTags.js";
import useATOL from "../hooks/useATOL.js";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import StaticContext from "../app/StaticContext/StaticContext.js";
import useMeasureSearchToFlightDetails from "../hooks/useMeasureSearchToFlightDetails.js";
import DSABanner from "../components/elements/DSABanner/DSABanner.mobile.js";
import useRemoveExternalParamsFromUrl from "../hooks/useRemoveExternalParamsFromUrl.js";
import { trackMetaChannelStagesV2 } from "../utils/experiments/track-meta-landings-v2.js";
import Frame from "../components/elements/Frame/index.js";
import scrollToTop from "../utils/scrollToTop.js";
import { useFlexDatesRectrictedPOS } from "../components/elements/FlexibleDateBestPrice/useFlexDatesRectrictedPOS.js";
import SearchResultBanner from "../components/elements/SearchResultBanner/SearchResultBanner.js";
import { actions as brandedFaresActions } from "../store/brandedFareOffers/actions.js";
import { useDispatch, useSelector } from "react-redux";
import { getSearchSegments } from "../store/search/selectors.js";
import { goal } from "@flights/et-universal";
import useFlightsAaWebFunnelSearchresutlTrafficFe from "../utils/experiments/funnel/flights_web_funnel_aa_fe_sr.js";
import flights_funnel_sr_auto_load_offers_mobile from "../utils/experiments/funnel/flights_funnel_sr_auto_load_offers_mobile.js";
import { useDelayResponse, useGetStaggeredSearchResults } from "../utils/experiments/funnel/flights_funnel_fake_staggered_search_web.js";
import { getSearchResultsVisibleIndexes } from "../store/searchResults/selectors.js";
import LoadingProgressBar from "../components/elements/SearchResults/LoadingProgressBar.js";
import { useFlightsWeb2StepMlRanker } from "../utils/experiments/funnel/flights_web_2_step_ml_ranker.js";
const Search = ({ getInitialState }) => {
    const store = useStore();
    const i18n = useI18n();
    const location = useLocation();
    const actions = useActions(searchResultsActions);
    const flexibleActions = useActions(flexibleDateMinPriceActions);
    const loaderActions = useActions(LoaderActions);
    const searchCriteriaActions = useActions(SearchCriteriaActions);
    const trackSRContextEventV2 = useTrackPageLoad("search_results");
    const trackSRSkeletonContextEventV2 = useTrackPageLoad("search_results_skeleton");
    const trackSRModalContextEventV2 = useTrackPageLoad("search_results_modal");
    const trackLandingPageOfferPriceAccuracyEvent = useLandingPageOfferPriceAccuracyEvent();
    const staticContext = useContext(StaticContext);
    const params = useParams();
    const { onFetchedResults, onSelectFlight } = useMeasureSearchToFlightDetails();
    const { flights, fetchStatus, error: fetchError, subsidizedFaresSummary, banners } = store.searchResults;
    const { visible: loadingScreenVisible } = store.loadingScreen;
    const isRestricedFlexDatePOS = useFlexDatesRectrictedPOS();
    const dispatch = useDispatch();
    const searchSegments = useSelector(getSearchSegments);
    const queryParams = new URLSearchParams(location.search);
    const searchAPIParams = useSearchAPIParams();
    const url = `/api/flights/?${searchAPIParams}`;
    const { trackStages } = useFlightsAaWebFunnelSearchresutlTrafficFe();
    useEffect(() => {
        trackStages();
    }, [trackStages]);
    const urlFlexibleDatePrice = useMemo(() => {
        const params = new URLSearchParams(searchAPIParams);
        params.set("useAggregateCache", "1");
        params.set("compareBestPrice", "1");
        return `/api/search/min-price/?${params.toString()}`;
    }, [searchAPIParams]);
    const { trackRenderTime } = useClientMetrics();
    const fireByeahSearchResultsTag = useFlightSearchResultsGTMTag();
    const { isATolProtectedSR } = useATOL();
    const [lastTrackedPageLoad, setLastTrackedPageLoad] = useState("");
    const humanRightBanner = banners?.find((banner) => banner.id === "LEGAL_NOTICE_HUMAN_RIGHTS");
    if (getInitialState && staticContext.set) {
        staticContext.set({ getInitialState: getInitialState(queryParams) });
    }
    useRemoveExternalParamsFromUrl();
    useEffect(() => {
        if (typeof window !== "undefined" &&
            window.location.search.includes("auto_load_page") &&
            flights.length > 0 &&
            !!flights_funnel_sr_auto_load_offers_mobile.trackWithDefaultStage())
            return;
        scrollToTop();
    }, [url]);
    useEffect(() => {
        trackMetaChannelStagesV2("mdot");
    }, []);
    useEffect(() => {
        goal("flights_search_results_page_view");
        goal("tbu_vertical_search_results_view");
    }, []);
    const hideActiveLoader = useCallback(() => {
        loaderActions.hide();
        const appShell = document.getElementById("app-shell");
        const firstAnchor = appShell?.querySelector("a");
        if (firstAnchor && document.activeElement === document.body)
            firstAnchor.focus();
    }, [loaderActions]);
    const delayResponse = useDelayResponse();
    const getStaggeredSearchResults = useGetStaggeredSearchResults();
    useFlightsWeb2StepMlRanker();
    useClientFetch(url, {
        condition: () => {
            if (isSearchStoreInitial(store.search))
                return false;
            return true;
        },
        fetch: () => {
            if (!location?.state?.hideLoader) {
                history.replaceState({ hideLoader: false }, document.title);
                loaderActions.show();
            }
            actions.fetch();
        },
        success: (data) => {
            const { variant, latency, visibleIndexes } = getStaggeredSearchResults(searchAPIParams, data.source === "cache");
            const processSuccess = () => {
                searchCriteriaActions.setSearchCriteria(getSearchCriteria(queryParams, store.search));
                if (data.flightOffers.length > 0) {
                    data.flightOffers[0].highlightInfo?.highlight &&
                        flightsSessionStore.set("highlightedOfferToken", data.flightOffers[0].token);
                    data.flightOffers.forEach((offer) => {
                        if (offer && !offer.requestableBrandedFares && offer.brandedFareOffers) {
                            dispatch(brandedFaresActions.fetchSuccess(offer.brandedFareOffers, offer.token));
                        }
                    });
                }
                if (latency && variant === 2) {
                    actions.hidePrice();
                    delayResponse(() => {
                        actions.showPrice();
                    }, latency);
                }
                if (latency && variant === 3 && visibleIndexes) {
                    actions.addVisibleIndexes(visibleIndexes);
                    delayResponse(() => {
                        actions.clearVisibleIndexes();
                    }, latency);
                }
                typeof window !== "undefined" &&
                    window.location.search.includes("auto_load_page") &&
                    !!flights_funnel_sr_auto_load_offers_mobile.trackWithDefaultStage()
                    ? actions.fetchSuccessAutoLoad(data)
                    : actions.fetchSuccess(data);
                trackLandingPageOfferPriceAccuracyEvent(data, location.search);
                fireByeahSearchResultsTag();
                hideActiveLoader();
                onFetchedResults();
            };
            if (latency && variant === 1) {
                delayResponse(() => {
                    processSuccess();
                }, latency);
            }
            else {
                processSuccess();
            }
        },
        error: (data) => {
            goal("flights_search_results_loading_error");
            hideActiveLoader();
            actions.fetchError(data);
        },
        finally: () => {
            if (!flights_funnel_sr_auto_load_offers_mobile.trackWithDefaultStage())
                return;
            const qs = new URLSearchParams(location.search);
            qs.delete("page");
            qs.delete("auto_load_page");
            history.replaceState({ hideLoader: false }, document.title, `${location.pathname}?${qs.toString()}`);
        },
        forceRefetch: location?.state?.forceRefetch
    });
    useClientFetch(urlFlexibleDatePrice, {
        fetch: () => flexibleActions.fetch(),
        success: (data) => flexibleActions.fetchSuccess(data),
        error: (data) => flexibleActions.fetchError(data),
        condition: () => {
            if (store.flexibleDateMinPrice.fetchStatus !== "success") {
                return store.search.cabinClass === "ECONOMY" && !isRestricedFlexDatePOS;
            }
            return false;
        },
        forceRefetch: location?.state?.forceRefetch
    });
    const header = useMemo(() => {
        return React.createElement(SearchHeader, null);
    }, []);
    const [preloadDetails, toggle] = useState(false);
    if (!preloadDetails && fetchStatus === "success") {
        toggle(true);
        FlightDetails.preload();
        Home.preload();
    }
    useEffect(() => {
        setTimeout(() => {
            hideActiveLoader();
        }, 6000);
    }, [loaderActions, hideActiveLoader]);
    useEffect(() => {
        if (["initial", "loading"].includes(fetchStatus)) {
            if (loadingScreenVisible && lastTrackedPageLoad !== "search_results_modal") {
                trackSRModalContextEventV2();
                setLastTrackedPageLoad("search_results_modal");
            }
            else if (lastTrackedPageLoad !== "search_results_skeleton") {
                trackSRSkeletonContextEventV2();
                setLastTrackedPageLoad("search_results_skeleton");
            }
        }
        else if (lastTrackedPageLoad !== "search_results") {
            trackSRContextEventV2();
            setLastTrackedPageLoad("search_results");
        }
    }, [fetchStatus]);
    const searchResultsVisibleIndexes = useSelector(getSearchResultsVisibleIndexes);
    if (useReloadIfSearchStoreMissing(store.search)) {
        return null;
    }
    isATolProtectedSR(store?.searchResults?.atolProtectedStatus);
    return (React.createElement(Profiler, { id: "screen", onRender: trackRenderTime },
        React.createElement(PageHeader, { segments: searchSegments }),
        React.createElement(AppShell, null,
            header,
            React.createElement(Frame, { mr: 4, mb: 4, ml: 4 },
                React.createElement("div", { style: { position: "relative" } }, !!searchResultsVisibleIndexes && React.createElement(LoadingProgressBar, { variant: "mobile" })),
                React.createElement(SearchResultBanner, { banner: humanRightBanner }),
                React.createElement(DSABanner, null),
                React.createElement(DestinationUkraineWarning, null),
                isSirfAvailable(subsidizedFaresSummary) && (React.createElement(SirfInfoBanner, { bannerName: "search-results", bannerContent: i18n.trans(t("flights_sr_spanish_discount_banner")) })),
                React.createElement(SearchResults, { airports: params?.airports, params: location.search, flights: flights, fetchStatus: fetchStatus, fetchError: fetchError, onSelectFlight: onSelectFlight }),
                isFeatureRunningClientSide("FLIGHTS_WEB_SR_OPT_IN_AA_NEETI_TRACKER") ? React.createElement(OptInAANeetiTracker, null) : undefined))));
};
export const PageHeader = ({ segments, children }) => {
    const i18n = useI18n();
    const fullRoute = useMemo(() => {
        const segmentsFromToList = segments.map(({ from, to }) => i18n.trans(t("flights_sr_page_title_route", {
            variables: { origin_city: getCitiesListForTitle(from), destination_city: getCitiesListForTitle(to) }
        })));
        return segmentsFromToList.join(" | ");
    }, [segments, i18n]);
    return (React.createElement(Helmet, null,
        React.createElement("title", null, i18n.trans(t("flights_sr_page_title", {
            variables: { full_route: fullRoute, b_companyname: COMPANY_NAME }
        }))),
        React.createElement("meta", { property: "webview:header", content: "regular" }),
        React.createElement("meta", { property: "webview:title", content: "" }),
        children));
};
const getCitiesListForTitle = (locations) => {
    return locations
        .map((location) => (location.type === "AIRPORT" ? location.cityName : location.name || location.code))
        .join(", ");
};
export default Search;
